import React from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const StarRating = ({ stars }) => {
  if (!stars || stars === 0) return null;
  return (
    <div className="flex items-center space-x-0.5">
      {[1, 2, 3]?.map(i => (
        <span key={i} className={`text-sm ${i <= stars ? 'opacity-100' : 'opacity-25'}`}>⭐</span>
      ))}
    </div>
  );
};

const TrailCard = ({ trail, onStart }) => {
  const navigate = useNavigate();

  const getStatusInfo = () => {
    if (trail?.studentProgress?.completedAt) {
      return {
        label: 'Completed',
        color: 'bg-success/10 text-success',
        icon: 'CheckCircle2'
      };
    }
    if (trail?.studentProgress?.completionPercentage > 0) {
      return {
        label: 'In Progress',
        color: 'bg-blue-500/10 text-blue-500',
        icon: 'PlayCircle'
      };
    }
    return {
      label: 'Not Started',
      color: 'bg-muted text-muted-foreground',
      icon: 'Circle'
    };
  };

  const statusInfo = getStatusInfo();
  const completionPercentage = trail?.studentProgress?.completionPercentage || 0;
  const starsEarned = trail?.studentProgress?.starsEarned || 0;

  const handleCardClick = () => {
    if (trail?.studentProgress) {
      navigate(`/student-learning-trail-view?trailId=${trail?.id}`);
    } else {
      onStart(trail?.id);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden hover-lift transition-organic group cursor-pointer" onClick={handleCardClick}>
      <div className="relative h-40 overflow-hidden">
        <Image
          src={trail?.imageUrl || '/assets/images/no_image.png'}
          alt={`${trail?.title} learning trail thumbnail`}
          className="w-full h-full object-cover group-hover:scale-105 transition-organic"
        />
        <div className="absolute top-3 right-3">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusInfo?.color} backdrop-blur-sm bg-white/80`}>
            {statusInfo?.label}
          </span>
        </div>
        {starsEarned > 0 && (
          <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1">
            <StarRating stars={starsEarned} />
          </div>
        )}
        {completionPercentage > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-2 bg-black/20">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-lg font-semibold text-foreground line-clamp-2 flex-1">
            {trail?.title}
          </h3>
        </div>

        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
          {trail?.description || 'Explore this learning trail'}
        </p>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <div className="flex items-center">
              <Icon name={statusInfo?.icon} size={16} className="mr-1" />
              <span>{completionPercentage}%</span>
            </div>
            {trail?.studentProgress?.startedAt && (
              <div className="flex items-center">
                <Icon name="Calendar" size={16} className="mr-1" />
                <span>Started {new Date(trail?.studentProgress?.startedAt)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              </div>
            )}
          </div>
          <Button
            variant={trail?.studentProgress ? 'default' : 'outline'}
            size="sm"
            iconName={trail?.studentProgress ? 'ArrowRight' : 'Play'}
            onClick={(e) => {
              e?.stopPropagation();
              handleCardClick();
            }}
          >
            {trail?.studentProgress ? 'Continue' : 'Start'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TrailCard;